// Master Job Card — Flexo Division (Annexure-I Operations Work Order).
// Full-form initial-details capture used by Sales & Operations before a job
// starts on the machine. The title/heading is editable so the same form can
// serve other divisions (Offset, Rotogravure, etc.) without duplication.
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Printer, Plus, Save, Eye, Copy, CheckCircle2, Cloud, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import { SelectWithAdd } from "@/components/SelectWithAdd";
import SubstrateCombobox from "@/components/SubstrateCombobox";
import OptionCombobox from "@/components/OptionCombobox";
import DatePicker from "@/components/DatePicker";
import QuickCustomerDialog from "@/components/orders/QuickCustomerDialog";
import CustomerCombobox from "@/components/CustomerCombobox";
import PoMatches from "@/components/orders/PoMatches";
import { useAuth } from "@/context/AuthContext";
import { aniloxRows, ANILOX_COMBOS, INK_MANUFACTURERS } from "@/components/Shared";

const DEFAULT_TITLE = "MEDPACK INDIA PVT. LTD. — OPERATIONS WORK ORDER — FLEXO DIVISION";
const today = () => new Date().toISOString().slice(0, 10);

// Fields worth asking for before a job is saved. `must` blocks the save; `should` is asked once.
const colourCount = (f) => Array.from({ length: 10 }, (_, i) => f[`colour_${i + 1}`]).filter((c) => String(c || "").trim()).length;
const blank = (v) => v === "" || v == null || v === false || (typeof v === "object" && !Array.isArray(v) && !Object.keys(v).length);
const emptyAnilox = (rows) => !(rows || []).some((r) => r && (r.anilox_combo || r.ink_manufacturer));
const LO_LI_TICKS = ["LO-1", "LO-2", "LO-3", "LO-4", "LI-1", "LI-2", "LI-3", "LI-4"];

/**
 * Master Job Card form.
 *
 * @param {Object}   props
 * @param {Function} props.onCreated   Called after a fresh create (New Inquiry / New Job dialogs)
 * @param {Function} props.onSaved     Called after edit-mode save (Job Detail tab)
 * @param {"job"|"inquiry"} props.mode
 * @param {Object}   props.initial     Existing job doc → renders in edit mode
 */
export default function MasterJobCardForm({ onCreated, onSaved, mode = "job", initial = null }) {
  const { user } = useAuth();
  const isEdit = !!initial;
  const isInquiry = mode === "inquiry" || (initial && (initial.status || "").toLowerCase() === "enquiry");
  const [title, setTitle] = useState(initial?.card_title || DEFAULT_TITLE);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  // Autosave / preview state
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved | error
  const autosaveTimer = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Signature state (loaded from initial, updated after sign-block calls)
  const [signatures, setSignatures] = useState({
    marketing_signed_by: initial?.marketing_signed_by || "",
    marketing_signed_at: initial?.marketing_signed_at || initial?.marketing_signed_date || "",
    operations_signed_by: initial?.operations_signed_by || "",
    operations_signed_at: initial?.operations_signed_at || initial?.operations_signed_date || "",
  });
  const [signHistory, setSignHistory] = useState(initial?.sign_history || []);

  // A per-user, per-mode key an autosave can write to; nothing reads it back (see the `form` useState below).
  const draftKey = isEdit ? null : `mjc-draft:${user?.id || "anon"}:${mode}`;

  const buildDefaults = (base = {}) => ({
      // Auto-issued / carry
      mpwo_number: base.mpwo_number || "", serial_no: base.serial_no || "",
      // Product Specifications
      product_code: base.product_code || "", mppl_customer_code: base.mppl_customer_code || "",
      customer_id: base.customer_id || "", customer_name: base.customer_name || "",
      product_name: base.product_name || "", substrate_type: base.substrate_type || "",
      substrate_code: base.substrate_code || "", substrate_description: base.substrate_description || "",
      gsm: base.gsm ?? "", manufactured_by: base.manufactured_by || "",
      label_length_mm: base.label_length_mm ?? "", label_width_mm: base.label_width_mm ?? "",
      special_sizes: base.special_sizes || "",
      no_of_colors: base.no_of_colors ?? "", varnish_type: base.varnish_type || "",
      print_type: base.print_type || "", color_matching: base.color_matching || "",
      finishing_details: base.finishing_details || "", embellishment: base.embellishment || "",
      form_of_supply: base.form_of_supply || "", shade_card: base.shade_card || "",
      colour_1: base.colour_1 || "", colour_2: base.colour_2 || "", colour_3: base.colour_3 || "",
      colour_4: base.colour_4 || "", colour_5: base.colour_5 || "", colour_6: base.colour_6 || "",
      colour_7: base.colour_7 || "", colour_8: base.colour_8 || "", colour_9: base.colour_9 || "",
      colour_10: base.colour_10 || "",
      colour_details: base.colour_details || "",
      labels_per_roll: base.labels_per_roll ?? "", labels_per_sheet: base.labels_per_sheet ?? "",
      sheets_per_bundle: base.sheets_per_bundle ?? "",
      sheet_form: !!base.sheet_form, roll_form: !!base.roll_form,
      print_one_side: !!base.print_one_side, print_front_back: !!base.print_front_back,
      any_special: base.any_special || "",
      cylinder_teeth: base.cylinder_teeth || "", cylinder_inches: base.cylinder_inches || "",
      cylinder_mm: base.cylinder_mm || "",
      serial_numbering: base.serial_numbering === true ? "yes" : base.serial_numbering === false ? "no" : (base.serial_numbering || ""),
      artwork_details_received: base.artwork_details_received || "",
      artwork_no: base.artwork_no || "", scanning_notes: base.scanning_notes || "",
      between_gap: base.between_gap || "", pdf_file_name: base.pdf_file_name || "",
      lo_li_ticks: (base.lo_li_ticks && typeof base.lo_li_ticks === "object") ? base.lo_li_ticks : {},
      core_diameter: base.core_diameter || "", core_other: base.core_other || "",
      winding_instructions: base.winding_instructions || "",
      proof_submitted_date: base.proof_submitted_date || "",
      proof_submitted_status: base.proof_submitted_status || "",
      proof_approved_date: base.proof_approved_date || "",
      proof_approved_status: base.proof_approved_status || "",
      shade_card_sent: base.shade_card_sent || "",
      shade_card_sent_status: base.shade_card_sent_status || "",
      // Availability check (tri-state Yes/No/NA)
      plate_available: base.plate_available || "",
      die_available: base.die_available || "",
      material_available: base.material_available || "",
      ink_available: base.ink_available || "",
      contact_person: base.contact_person || "", contact_phone: base.contact_phone || "",
      delivery_details: base.delivery_details || base.delivery_address || "",
      marketing_signed_by: base.marketing_signed_by || "", marketing_signed_date: base.marketing_signed_date || "",
      // Work Execution
      no_of_ups: base.no_of_ups ?? "", ups_across: base.ups_across ?? "", ups_along: base.ups_along ?? "",
      paper_width_mm: base.paper_width_mm ?? "", gap_across_mm: base.gap_across_mm ?? "",
      planned_good_meter: base.planned_good_meter ?? "",
      planned_waste_meter: base.planned_waste_meter ?? "", excess_qty: base.excess_qty ?? "",
      die_code: base.die_code || "", die_size: base.die_size || "", no_of_ups_die: base.no_of_ups_die ?? "",
      anilox_details: (() => {
        // First, use MJC's saved anilox_details. If empty, fall back to the job's Printing
        // production log — that lets a repeat job inherit the anilox setup (and ink manufacturer)
        // automatically without the operator retyping it. Pantone itself is never carried here —
        // aniloxRows() always derives it live from this form's own colour_1..colour_8 below.
        let src = Array.isArray(base.anilox_details) ? base.anilox_details : [];
        if (!src.some((r) => r && (r.anilox_combo || r.ink_manufacturer))) {
          const printingLog = base?.stages?.printing?.data?.anilox_details || [];
          if (Array.isArray(printingLog) && printingLog.length) src = printingLog;
        }
        return aniloxRows(src, base);
      })(),
      operations_signed_by: base.operations_signed_by || "",
      shade_card_submitted_on: base.shade_card_submitted_on || "",
      shade_card_submitted_status: base.shade_card_submitted_status || "",
      shade_card_approved_on: base.shade_card_approved_on || "",
      shade_card_approved_status: base.shade_card_approved_status || "",
      quantity: base.quantity ?? (isInquiry ? 500 : ""),
      unit_price: base.unit_price ?? 0,
      po_number: base.po_number || "",
      po_date: base.po_date || "",
      hpdf_approved: base.hpdf_approved || "",
      order_confirmed_by: base.order_confirmed_by || "",
      date_received: base.date_received || (isEdit ? "" : today()),
      bpr_issue_date: base.bpr_issue_date || (isEdit ? "" : today()),
      due_date: base.due_date || "",
      job_type: base.job_type || (base.repeat_orders?.length > 0 ? "Repeat Job" : "New Job"),
      card_title: base.card_title || DEFAULT_TITLE,
      repeat_orders: (() => {
        const src = Array.isArray(base.repeat_orders) ? base.repeat_orders : [];
        const blank = () => ({
          sl: 0, wo_no: "", po_no: "", qty: "", delivery_date: "", mkt_sign: "",
          print_date: "", issued_rmts: "", printed_rmts: "", waste_rmts: "", op_sign: "",
          dispatch_date: "", qty_delivered: "", dc_no: "", dispatch_sign: "", remarks: "",
        });
        // Normalize each row so nulls/strings/etc. never blow up render
        const clean = src.map((r, i) => (
          r && typeof r === "object" ? { ...blank(), ...r, sl: r.sl ?? i + 1 } : { ...blank(), sl: i + 1 }
        ));
        if (!clean.length) clean.push({ ...blank(), sl: 1 });
        return clean;
      })(),
    });

  // New Job always starts blank — a saved draft is write-only (see the autosave effect below): it exists purely
  // so the raw localStorage entry could be recovered by hand after a genuine crash, but nothing auto-fills the
  // form from it, on this mount or any later one, so reopening never brings back what was typed last time.
  const [form, setForm] = useState(() => buildDefaults(initial || {}));

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const requiresPo = !isEdit && !isInquiry
    && (form.order_confirmed_by || "").trim().toLowerCase() !== "verbal";

  const reloadCustomers = (autoSelectId) =>
    api.get("/customers", { params: { limit: 100 } })
      .then(({ data }) => {
        setCustomers(data);
        // Pick from the freshly fetched list — not the stale `customers` closure
        if (autoSelectId) {
          const c = data.find((x) => x.id === autoSelectId);
          if (c) setForm((f) => ({
            ...f,
            customer_id: c.id,
            customer_name: c.party_name,
            delivery_details: f.delivery_details || c.delivery_address || c.corporate_address || "",
            contact_person: f.contact_person || c.contact_person || "",
            contact_phone: f.contact_phone || c.phone || "",
          }));
        }
      })
      .catch(() => {});

  useEffect(() => {
    reloadCustomers();
    if (!isEdit) {
      api.get("/jobs/next-numbers", { params: { scope: isInquiry ? "inquiry" : "job" } })
        .then(({ data }) => setForm((f) => ({
          ...f,
          mpwo_number: f.mpwo_number || data.mpwo_number || "",
          serial_no: f.serial_no || data.serial_no || "",
        })))
        .catch(() => {});
    }
  }, [isInquiry, isEdit, draftKey]);

  const submittedRef = useRef(false);
  const draftTimerRef = useRef(null);

  // Persist create-mode drafts to localStorage as the user types (debounced).
  // Cleared on successful submit so the next create starts fresh.
  useEffect(() => {
    if (isEdit || !draftKey || submittedRef.current) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (submittedRef.current) return;              // race: submit succeeded during the debounce
      try {
        window.localStorage.setItem(draftKey, JSON.stringify({
          form, __saved_at: Date.now(),
        }));
        setAutosaveStatus("saved");
      } catch { setAutosaveStatus("error"); }
    }, 500);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [form, draftKey, isEdit]);

  const clearDraft = () => {
    submittedRef.current = true;                     // block any in-flight autosave from re-writing
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    try { draftKey && window.localStorage.removeItem(draftKey); } catch { /* noop */ }
  };

  // Closing the New Job dialog without submitting (the X, Escape, clicking outside) means "never mind" —
  // the next "New Job" should open blank, not bring back this customer/product/whatever was typed. Only a
  // successful submit (which already calls clearDraft itself, making this a harmless no-op then) keeps it
  // around in between; nothing else should resurrect a half-filled form days later.
  useEffect(() => () => { if (!isEdit && draftKey && !submittedRef.current) clearDraft(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Accepts either the full customer object (preferred — comes from
  // CustomerCombobox.onSelect and covers customers outside the prefetch) or a
  // bare id (kept for the QuickAdd flow that only returns the new id).
  const pickCustomer = (customerOrId) => {
    let c = customerOrId;
    if (customerOrId && typeof customerOrId === "string") {
      c = customers.find((x) => x.id === customerOrId);
    }
    if (!c) {
      setForm((f) => ({ ...f, customer_id: "", customer_name: "" }));
      return;
    }
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: c.party_name,
      delivery_details: f.delivery_details || c.delivery_address || c.corporate_address || "",
      contact_person: f.contact_person || c.contact_person || "",
      contact_phone: f.contact_phone || c.phone || "",
    }));
  };

  const updAnilox = (idx, key, val) => {
    setForm((f) => {
      const rows = [...f.anilox_details];
      rows[idx] = { ...rows[idx], [key]: val };
      return { ...f, anilox_details: rows };
    });
  };

  // Fields the system can work out itself are filled while the form is open, and only while empty:
  // ups from across x down, colours from the colour boxes, Die Size from the label's own length x
  // width (a die is built to that exact size — verified against real jobs: a given label size is
  // saved under the same die size every time), Paper Width from label width + gap across x ups
  // across + a fixed trim allowance (a rough starting figure only — see the note below it; the real
  // stock width is a planning choice, confirmed NOT predictable from label/die/ups even when they
  // match exactly across jobs), Required Metres from order qty / ups per repeat x cylinder — this
  // one IS verified: checked against the master Planning workbook's own figures, matches to within
  // rounding on every normal-quantity row. Typing over any of these is respected.
  const derived = useRef({});
  useEffect(() => {
    setForm((f) => {
      const next = {};
      const ups = (Number(f.ups_across) || 0) * (Number(f.ups_along) || 0);
      const colours = colourCount(f);
      const labelW = Number(f.label_width_mm) || 0;
      const labelL = Number(f.label_length_mm) || 0;
      const upsAcross = Number(f.ups_across) || 0;
      const gapAcross = Number(f.gap_across_mm) || 0;
      const width = labelW > 0 && upsAcross > 0 ? Math.round((labelW + gapAcross) * upsAcross + 12) : 0;
      const dieSize = labelW > 0 && labelL > 0 ? `${labelW}X${labelL}` : "";
      const qty = Number(f.quantity) || 0;
      const cylinderMm = Number(f.cylinder_mm) || 0;
      const goodMeter = qty > 0 && ups > 0 && cylinderMm > 0
        ? Math.round((Math.ceil(qty / ups) * cylinderMm / 1000) * 10) / 10 : 0;
      // Required Metres (Good) is a hard formula off Qty/Ups/Cylinder, not a rough suggestion like the
      // fields below — it always tracks those three live, overwriting whatever was there before, so it
      // never goes stale behind the "Calculated:" hint text shown under it.
      if (qty > 0 && ups > 0 && cylinderMm > 0 && String(f.planned_good_meter) !== String(goodMeter)) {
        next.planned_good_meter = goodMeter;
      }
      // Cylinder Repeat — Teeth/Inches always track whatever MM is on the form, however it got there
      // (picked, typed, or auto-filled from a product's master card) — the cheat-sheet row when MM
      // matches one, else the same 3.175mm-per-tooth / 8-teeth-per-inch formula as the dropdowns.
      if (cylinderMm > 0) {
        const row = CYL_REPEAT_ROWS.find((r) => Number(r.mm) === cylinderMm);
        const teeth = row ? row.teeth : String(round(cylinderMm / 3.175, 0));
        const inches = row ? row.inches : String(round(cylinderMm / 3.175 / 8, 2));
        if (String(f.cylinder_teeth) !== teeth) next.cylinder_teeth = teeth;
        if (String(f.cylinder_inches) !== inches) next.cylinder_inches = inches;
      }
      const want = { no_of_ups: ups, no_of_ups_die: ups, no_of_colors: colours, paper_width_mm: width };
      for (const [k, v] of Object.entries(want)) {
        // A loaded value that already matches the live formula (e.g. an existing job whose saved
        // figure was itself auto-calculated) counts as derived too, so later edits keep it in sync —
        // only a value that genuinely differs from the formula is treated as a deliberate override.
        if (derived.current[k] === undefined && v > 0 && String(f[k]) === String(v)) derived.current[k] = v;
        const free = f[k] === "" || f[k] == null || String(f[k]) === String(derived.current[k]);
        if (v > 0 && free && String(f[k]) !== String(v)) { next[k] = v; derived.current[k] = v; }
      }
      if (dieSize && derived.current.die_size === undefined && f.die_size === dieSize) derived.current.die_size = dieSize;
      const dieFree = f.die_size === "" || f.die_size == null || f.die_size === derived.current.die_size;
      if (dieSize && dieFree && f.die_size !== dieSize) { next.die_size = dieSize; derived.current.die_size = dieSize; }
      return Object.keys(next).length ? { ...f, ...next } : f;
    });
  }, [form.ups_across, form.ups_along, form.colour_1, form.colour_2, form.colour_3, form.colour_4, form.colour_5,
      form.colour_6, form.colour_7, form.colour_8, form.colour_9, form.colour_10,
      form.label_width_mm, form.label_length_mm, form.gap_across_mm, form.quantity, form.cylinder_mm]);

  // Date Received (the order confirmed date) is the latest of HPDF approved, PO date and shade card
  // approved; Job Due Date is 10 days after it. BPR Issue Date is entered by hand and plays no part.
  // Both are starting points, not rules: typing over one (or clearing it) is always respected.
  const dateDerived = useRef(isEdit ? null : today());
  const dueDerived = useRef(null);
  useEffect(() => {
    const day = (v) => (/^\d{4}-\d{2}-\d{2}/.test(v || "") ? v.slice(0, 10) : "");
    const latest = [form.hpdf_approved, form.po_date, form.shade_card_approved_on].map(day).filter(Boolean).sort().pop();
    if (isEdit || !latest) return;  // new jobs only: an existing job keeps the dates it has
    setForm((f) => {
      if (!(f.date_received === "" || f.date_received == null || f.date_received === dateDerived.current)) return f;
      if (f.date_received === latest) return f;
      dateDerived.current = latest;
      return { ...f, date_received: latest };
    });
  }, [form.hpdf_approved, form.po_date, form.shade_card_approved_on]);
  useEffect(() => {
    if (isEdit || !form.date_received) return;
    setForm((f) => {
      const free = f.due_date === "" || f.due_date == null || f.due_date === dueDerived.current;
      if (!free) return f;
      const d = new Date(f.date_received);
      if (Number.isNaN(d.getTime())) return f;
      d.setDate(d.getDate() + 10);
      const next = d.toISOString().slice(0, 10);
      if (f.due_date === next) return f;
      dueDerived.current = next;
      return { ...f, due_date: next };
    });
  }, [form.date_received]);

  // A job carrying a substrate code but not its linked fields (common on register-imported jobs,
  // saved before the Substrate Code picker was in place) gets them backfilled from the substrate
  // master once, on load — same data `onPick` above would have set, just not blocked on a re-pick.
  const substrateBackfilled = useRef(false);
  useEffect(() => {
    if (substrateBackfilled.current || !form.substrate_code || form.substrate_description) return;
    substrateBackfilled.current = true;
    api.get("/substrates", { params: { q: form.substrate_code, limit: 10 } })
      .then(({ data }) => {
        const s = data.find((x) => String(x.code).trim().toUpperCase() === String(form.substrate_code).trim().toUpperCase());
        if (!s) return;
        setForm((f) => (f.substrate_description ? f : {
          ...f,
          _substrate_ref: f._substrate_ref || s,
          substrate_description: s.description || f.substrate_description,
          substrate_type: f.substrate_type || s.substrate_type,
          gsm: f.gsm === "" || f.gsm == null ? s.gsm : f.gsm,
          manufactured_by: f.manufactured_by || s.manufacturer,
        }));
      })
      .catch(() => {});
  }, [form.substrate_code, form.substrate_description]);

  // What is still missing: `must` stops the save, `should` is asked about once.
  const gaps = () => {
    const must = [], should = [];
    const ask = (list, key, label, empty) => { if (empty) list.push({ key, label }); };
    ask(must, "customer", "Customer", !form.customer_name);
    ask(must, "product_name", "Name of the Product", !String(form.product_name || "").trim());
    if (!isInquiry) {
      ask(must, "quantity", "Order Qty", !(Number(form.quantity) > 0));
      ask(must, "po_number", "PO Number (or set Order Confirmed By to Verbal)", requiresPo && !String(form.po_number || "").trim());
      ask(should, "unit_price", "Unit Price", !(Number(form.unit_price) > 0));
      ask(should, "due_date", "Job Due Date", !form.due_date);
    }
    ask(should, "substrate_type", "Type of Substrate", !form.substrate_type);
    ask(should, "label_length_mm", "Label Length (mm)", blank(form.label_length_mm));
    ask(should, "label_width_mm", "Label Width (mm)", blank(form.label_width_mm));
    // Planning-spec fields: mandatory once there is a card to plan (edit mode), whether filled by
    // hand or by the previous-job auto-fill — a card still missing one (no match found, or someone
    // cleared it) must stop the save same as if it was never touched. Not forced at New Job /
    // Inquiry intake, where this spec is often genuinely not known yet (that's what "To be planned" is for).
    const list = isEdit ? must : should;
    ask(list, "no_of_colors", "Total No. of Colours", blank(form.no_of_colors));
    ask(list, "varnish_type", "Type of Varnish", !form.varnish_type);
    ask(list, "print_type", "Print Type", !form.print_type);
    ask(list, "color_matching", "Color Matching", !form.color_matching);
    ask(list, "finishing_details", "Finishing", !form.finishing_details);
    ask(list, "embellishment", "Embellishment", !form.embellishment);
    ask(list, "form_of_supply", "Form of Supply", !form.form_of_supply);
    if (form.form_of_supply === "Roll Form") {
      ask(list, "labels_per_roll", "No. of Labels Per Roll", blank(form.labels_per_roll));
    } else if (form.form_of_supply === "Sheet Form") {
      ask(list, "sheets_per_bundle", "No. of Sheets Per Bundle", blank(form.sheets_per_bundle));
      ask(list, "labels_per_sheet", "No. of Labels Per Sheet", blank(form.labels_per_sheet));
    }
    return { must, should };
  };
  const [askGaps, setAskGaps] = useState(null);
  const goTo = (key) => {
    setAskGaps(null);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="mjc-${key}"]`);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus?.();
    }, 120);
  };

  // "Save as Job ID" (new jobs only, Operations Head) and a plain "Save Changes" on a job still
  // "To be planned" (no BPR yet) both skip the must-fill check below — that's the whole point,
  // the details aren't known yet. Only "convert" (issuing the real BPR) requires them.
  const toBePlanned = isEdit && !initial?.mpwo_number;
  const submit = (e, saveMode = "bpr") => {
    e.preventDefault();
    if (saveMode === "jobid" || (toBePlanned && saveMode !== "convert")) {
      // A job still needs to be identifiable at minimum — who it's for and what product — so
      // this can't silently save a blank row with nothing to go on.
      const minGaps = [];
      if (!form.customer_name) minGaps.push({ key: "customer", label: "Customer" });
      if (!String(form.product_code || "").trim()) minGaps.push({ key: "product_code", label: "Product Code" });
      if (minGaps.length) {
        toast.error(`Fill in: ${minGaps.map((m) => m.label).join(", ")}`);
        goTo(minGaps[0].key);
        return;
      }
      save(saveMode);
      return;
    }
    const { must, should } = gaps();
    if (must.length) {
      toast.error(`Fill in: ${must.map((m) => m.label).join(", ")}`);
      goTo(must[0].key);
      return;
    }
    if (!isEdit && should.length) { setAskGaps(should); return; }
    save(saveMode);
  };

  const save = async (saveMode = "bpr") => {
    setAskGaps(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        card_title: title,
        // "jobid" is the one save that must land on "To be planned", not "pending" — leaving
        // status out of the payload here (rather than hardcoding "pending") lets the backend's
        // own "To be planned" default for assign_bpr=false actually take effect.
        status: isEdit ? form.status : (saveMode === "jobid" ? undefined : (isInquiry ? "Enquiry" : "pending")),
        inquiry_type: isInquiry ? (form.inquiry_type || "New Inquiry") : undefined,
        gsm: form.gsm === "" ? undefined : Number(form.gsm),
        label_length_mm: form.label_length_mm === "" ? undefined : Number(form.label_length_mm),
        label_width_mm: form.label_width_mm === "" ? undefined : Number(form.label_width_mm),
        no_of_colors: form.no_of_colors === "" ? undefined : parseInt(form.no_of_colors),
        labels_per_roll: form.labels_per_roll === "" ? undefined : parseInt(form.labels_per_roll),
        labels_per_sheet: form.labels_per_sheet === "" ? undefined : parseInt(form.labels_per_sheet),
        sheets_per_bundle: form.sheets_per_bundle === "" ? undefined : parseInt(form.sheets_per_bundle),
        ups_across: form.ups_across === "" ? undefined : parseInt(form.ups_across),
        ups_along: form.ups_along === "" ? undefined : parseInt(form.ups_along),
        gap_across_mm: form.gap_across_mm === "" ? undefined : Number(form.gap_across_mm),
        paper_width_mm: form.paper_width_mm === "" ? undefined : Number(form.paper_width_mm),
        planned_good_meter: form.planned_good_meter === "" ? undefined : Number(form.planned_good_meter),
        planned_waste_meter: form.planned_waste_meter === "" ? undefined : Number(form.planned_waste_meter),
        excess_qty: form.excess_qty === "" ? undefined : Number(form.excess_qty),
        quantity: Number(form.quantity) || 1,
        unit_price: Number(form.unit_price) || 0,
        // "Form of Supply" is now a single pick; the two legacy booleans are derived from it so
        // older views that still fall back to job.roll_form / job.sheet_form keep working.
        roll_form: form.form_of_supply === "Roll Form",
        sheet_form: form.form_of_supply === "Sheet Form",
        anilox_details: aniloxRows(form.anilox_details, form).filter((r) => r.anilox_combo || r.ink_manufacturer),
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
        // Strip UI-only "_"-prefixed hints (e.g. _substrate_ref) so they never
        // leak into the persisted job document.
        if (k.startsWith("_")) delete payload[k];
      });
      if (isEdit) {
        const { data } = await api.patch(`/jobs/${initial.id}`, payload);
        if (saveMode === "convert") {
          const { data: converted } = await api.post(`/jobs/${initial.id}/assign-bpr`);
          toast.success(`Converted to BPR · ${converted.mpwo_number}`);
          onSaved && onSaved(converted);
        } else {
          toast.success(`Master Job Card saved · ${data.mpwo_number || data.job_number}`);
          onSaved && onSaved(data);
        }
      } else {
        payload.assign_bpr = saveMode !== "jobid";
        // `form.mpwo_number` is only ever a *preview* of the next available number (fetched on
        // mount purely to show "BPR No. ..." on screen — see the /jobs/next-numbers effect above),
        // never a real, issued one. Saving as "To be planned" must not submit that preview as if
        // it were a real explicit BPR — the backend would then use it for real instead of leaving
        // the job without one.
        if (saveMode === "jobid") payload.mpwo_number = "";
        const { data } = await api.post("/jobs", payload);
        toast.success(saveMode === "jobid"
          ? `Job ${data.job_number} saved as To be planned — open it later to fill in the rest and convert to BPR`
          : `${isInquiry ? "Inquiry" : "Job"} ${data.job_number} created · ${data.mpwo_number}`);
        clearDraft();
        onCreated && onCreated(data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const printCard = () => {
    if (!initial?.id) { toast.error("Save the card first, then print"); return; }
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${initial.id}/master-card.pdf`, "_blank");
  };
  const printBpr = () => {
    if (!initial?.id) { toast.error("Save the card first, then print the BPR slip"); return; }
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${initial.id}/bpr-slip.pdf`, "_blank");
  };

  // ---- Inline PDF Preview (via authenticated fetch → blob URL) ------------
  const openPreview = async (endpoint) => {
    if (!initial?.id) { toast.error("Save the card first"); return; }
    try {
      const res = await api.get(`/jobs/${initial.id}/${endpoint}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      // Free the previous blob URL if any
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Sign block (Marketing / Operations) --------------------------------
  const signBlock = async (block) => {
    if (!initial?.id) { toast.error("Save the card first, then sign"); return; }
    try {
      const { data } = await api.post(`/jobs/${initial.id}/sign-block`, { block });
      setSignatures((s) => ({ ...s, ...data }));
      if (data.history_entry) {
        setSignHistory((h) => [...h, data.history_entry]);
      }
      // Mirror into the form so autosave doesn't clobber it
      setForm((f) => ({ ...f, ...data }));
      toast.success(`${block === "marketing" ? "Marketing" : "Operations"} block signed`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---- Autosave (debounced) — edit mode only ------------------------------
  // Reason: creating a job on every keystroke would spam job numbers, so we
  // only autosave once we already have an id. Debounced 800ms after the last
  // change.
  useEffect(() => {
    if (!isEdit) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setAutosaveStatus("saving");
      try {
        const patch = {
          ...form,
          card_title: title,
          // `form` carries no status field of its own — without this, the PATCH below has no
          // "status" key at all, which (on the server) is read as "status wasn't touched, check
          // whether enough spec fields are now filled to auto-promote 'To be planned' to
          // 'Converted BPR'". That auto-promotion exists for the old manual Planning Spec
          // workflow; here it silently fires off this autosave within seconds of opening a
          // repeat job's form, the instant the previous-card auto-fill fills enough fields —
          // never a click, never a real decision to convert. Always sending the job's current,
          // unchanged status keeps autosave from ever triggering it.
          status: initial.status,
          gsm: form.gsm === "" ? undefined : Number(form.gsm),
          label_length_mm: form.label_length_mm === "" ? undefined : Number(form.label_length_mm),
          label_width_mm: form.label_width_mm === "" ? undefined : Number(form.label_width_mm),
          no_of_colors: form.no_of_colors === "" ? undefined : parseInt(form.no_of_colors),
          labels_per_roll: form.labels_per_roll === "" ? undefined : parseInt(form.labels_per_roll),
          labels_per_sheet: form.labels_per_sheet === "" ? undefined : parseInt(form.labels_per_sheet),
          sheets_per_bundle: form.sheets_per_bundle === "" ? undefined : parseInt(form.sheets_per_bundle),
          ups_across: form.ups_across === "" ? undefined : parseInt(form.ups_across),
          ups_along: form.ups_along === "" ? undefined : parseInt(form.ups_along),
          gap_across_mm: form.gap_across_mm === "" ? undefined : Number(form.gap_across_mm),
          paper_width_mm: form.paper_width_mm === "" ? undefined : Number(form.paper_width_mm),
          planned_good_meter: form.planned_good_meter === "" ? undefined : Number(form.planned_good_meter),
          planned_waste_meter: form.planned_waste_meter === "" ? undefined : Number(form.planned_waste_meter),
          excess_qty: form.excess_qty === "" ? undefined : Number(form.excess_qty),
          roll_form: form.form_of_supply === "Roll Form",
          sheet_form: form.form_of_supply === "Sheet Form",
          anilox_details: aniloxRows(form.anilox_details, form).filter((r) => r.anilox_combo || r.ink_manufacturer),
        };
        Object.keys(patch).forEach((k) => {
          if (patch[k] === undefined) delete patch[k];
          if (k.startsWith("_")) delete patch[k];   // UI-only hints — never persist
        });
        await api.patch(`/jobs/${initial.id}`, patch);
        setAutosaveStatus("saved");
      } catch { setAutosaveStatus("error"); }
    }, 800);
    return () => autosaveTimer.current && clearTimeout(autosaveTimer.current);
  }, [form, title, isEdit, initial?.id]);

  // ---- Load from previous job — create/inquiry mode only ------------------
  const [prevHint, setPrevHint] = useState(null);   // { source_mpwo, card }
  useEffect(() => {
    if (isEdit) return;
    const { customer_name, product_code, product_name, mppl_customer_code } = form;
    // either Customer Product Code — Old or — New is enough to find the master card; whichever
    // one the customer actually gives varies, and the card has both so either one finds it
    if (!customer_name || (!product_code && !product_name && !mppl_customer_code)) { setPrevHint(null); return; }
    const t = setTimeout(() => {
      api.get("/jobs/lookup/last-card", {
        params: { customer_name, product_code: product_code || undefined, product_name: product_name || undefined, mppl_customer_code: mppl_customer_code || undefined },
      })
        .then(({ data }) => setPrevHint(data.found ? data : null))
        .catch(() => setPrevHint(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.customer_name, form.product_code, form.product_name, form.mppl_customer_code, isEdit]);

  // The first time a card is found for this customer + product, its empty fields are filled in at once
  // (nothing already typed is touched); "Load fields" below still overwrites everything on request.
  const appliedFor = useRef("");
  useEffect(() => {
    if (isEdit || !prevHint?.card) return;
    const src = prevHint.source_mpwo || prevHint.source_job_number;
    if (appliedFor.current === src) return;
    appliedFor.current = src;
    setForm((f) => {
      const next = { ...f };
      for (const [k, v] of Object.entries(prevHint.card)) {
        if (k === "anilox_details") {
          if (emptyAnilox(f.anilox_details) && Array.isArray(v) && v.length) next[k] = aniloxRows(v, f);
        } else if (k === "lo_li_ticks" ? blank(f[k]) || !Object.values(f[k]).some(Boolean) : blank(f[k]) && !blank(v)) {
          next[k] = v;
        }
      }
      if (prevHint.last_price && !(Number(f.unit_price) > 0)) { next.unit_price = prevHint.last_price; }
      if (!isInquiry && f.job_type === "New Job") { next.job_type = "Repeat Job"; }
      return next;
    });
    if (prevHint.card.card_title) setTitle(prevHint.card.card_title);
    setTimeout(() => toast.success(`Filled from ${src}${prevHint.last_price ? " (including its last price, check it)" : ""}`), 0);
  }, [prevHint, isEdit, isInquiry]);

  const loadFromPrev = () => {
    if (!prevHint?.card) return;
    setForm((f) => ({
      ...f,
      ...prevHint.card,
      anilox_details: aniloxRows(prevHint.card.anilox_details, { ...f, ...prevHint.card }),
      // Preserve identity + freshly auto-issued numbers
      mpwo_number: f.mpwo_number,
      serial_no: f.serial_no,
      customer_id: f.customer_id,
      customer_name: f.customer_name,
      product_name: f.product_name || prevHint.card.product_name,
    }));
    if (prevHint.card.card_title) setTitle(prevHint.card.card_title);
    toast.success(`Loaded from ${prevHint.source_mpwo || prevHint.source_job_number}`);
  };

  return (
    <form onSubmit={submit} className="space-y-6 text-sm">
      {/* Card Heading — presets dropdown (Flexo Division, Offset, Roto…) with "add new" */}
      <div className="border-b-2 border-neutral-900 pb-3">
        <div className="text-center font-bold text-base uppercase tracking-wide text-neutral-900 mb-2" data-testid="mjc-title">
          {title}
        </div>
        <div className="flex items-center gap-2 justify-center">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider">Card Title</span>
          <div className="w-[420px]">
            <SelectWithAdd
              optionsKey="card_title"
              value={title}
              onChange={setTitle}
              testId="mjc-title-preset"
              placeholder="Pick or add a card heading…"
            />
          </div>
        </div>
        <div className="mt-2 text-[10px] text-neutral-500 flex justify-center gap-4 font-mono-num">
          <span>DOC No. RMOWO-00</span>
          <span>·</span>
          <span>BPR No. <b data-testid="mjc-mpwo">{form.mpwo_number || "…"}</b></span>
          <span>·</span>
          <span>Serial No. <b data-testid="mjc-serial">{form.serial_no || "…"}</b></span>
          {isEdit && (
            <>
              <span>·</span>
              <AutosaveBadge status={autosaveStatus} />
            </>
          )}
        </div>
      </div>

      {/* Load from Previous Job — create/inquiry mode only, when a match is found */}
      {!isEdit && prevHint && (
        <div
          data-testid="mjc-prev-hint"
          className="flex items-center gap-3 border border-violet-200 bg-violet-50 px-4 py-3 rounded-sm"
        >
          <Copy size={14} className="text-violet-700 shrink-0" />
          <div className="text-xs text-violet-900 flex-1">
            Found a previous card for this customer + product —
            <span className="font-mono-num font-semibold ml-1">{prevHint.source_mpwo || prevHint.source_job_number}</span>
            {prevHint.source_created_at && (
              <span className="text-violet-600"> ({(prevHint.source_created_at || "").slice(0, 10)})</span>
            )}
            . Its details are filled in where the form was empty. Load all of it, replacing what is typed?
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="mjc-load-prev"
            onClick={loadFromPrev}
            className="rounded-sm border-violet-400 text-violet-800 hover:bg-violet-100"
          >
            <Copy size={12} className="mr-1.5" /> Replace all
          </Button>
        </div>
      )}

      {/* Product Specifications */}
      <MjcSection title="Product Specifications" hint="To be filled by the Marketing Department after negotiations and finalization of the Customer's Purchase Order.">
        <MjcField label="Customer Product Code — Old" testId="mjc-product_code" value={form.product_code} onChange={(v) => upd("product_code", v)} />
        <MjcField label="Customer Product Code — New" testId="mjc-mppl_customer_code" value={form.mppl_customer_code || ""} onChange={(v) => upd("mppl_customer_code", v)} />
        <div>
          <MjcLabel label="Job Type" />
          <button
            type="button"
            data-testid="mjc-job_type"
            onClick={() => upd("job_type", form.job_type === "Repeat Job" ? "New Job" : "Repeat Job")}
            className={`h-9 w-full rounded-sm border text-sm font-medium transition-colors ${
              form.job_type === "Repeat Job"
                ? "bg-emerald-100 border-emerald-400 text-emerald-800"
                : "bg-[#FF5A00] border-[#FF5A00] text-white"
            }`}
          >
            {form.job_type || "New Job"}
          </button>
        </div>
        <MjcCustomer customers={customers} form={form} onPick={pickCustomer} onReload={reloadCustomers} setForm={setForm} />
        <MjcField label="Name of the Product *" testId="mjc-product_name" value={form.product_name} onChange={(v) => upd("product_name", v)} required full />
        <MjcSelectAdd label="Type of Substrate" optionsKey="substrate_type" value={form.substrate_type} onChange={(v) => upd("substrate_type", v)} testId="mjc-substrate_type" />
        <div>
          <label className="label-caps block mb-1.5">Substrate Code</label>
          <SubstrateCombobox
            testId="mjc-substrate_code"
            // Fall back to a synthesized ref built from the persisted values so
            // the trigger always shows the saved code even when the operator
            // hasn't opened the combobox this session.
            value={form._substrate_ref || (form.substrate_code
              ? { code: form.substrate_code, description: form.substrate_description || "", gsm: form.gsm, manufacturer: form.manufactured_by, substrate_type: form.substrate_type }
              : null)}
            onPick={(s) => {
              // Picking a substrate hydrates the linked spec fields so the
              // operator never has to retype GSM + Manufacturer.
              setForm((f) => ({
                ...f,
                _substrate_ref: s,
                substrate_code: s.code,
                substrate_description: s.description || f.substrate_description,
                substrate_type: s.substrate_type || f.substrate_type,
                gsm: s.gsm ?? f.gsm,
                manufactured_by: s.manufacturer || f.manufactured_by,
              }));
            }}
          />
        </div>
        <MjcField label="Substrate Description" testId="mjc-substrate_description" value={form.substrate_description || ""} onChange={(v) => upd("substrate_description", v)} full />
        <MjcField label="GSM" type="number" testId="mjc-gsm" value={form.gsm} onChange={(v) => upd("gsm", v)} />
        <MjcSelectAdd label="Manufactured by" optionsKey="manufacturer" value={form.manufactured_by} onChange={(v) => upd("manufactured_by", v)} testId="mjc-manufactured_by" />
        <MjcField label="Label Length (mm)" type="number" testId="mjc-label_length_mm" value={form.label_length_mm} onChange={(v) => upd("label_length_mm", v)} />
        <MjcField label="Label Width (mm)" type="number" testId="mjc-label_width_mm" value={form.label_width_mm} onChange={(v) => upd("label_width_mm", v)} />
        <MjcArea label="If special sizes" testId="mjc-special_sizes" value={form.special_sizes} onChange={(v) => upd("special_sizes", v)} full />
        <MjcField label="Total No. of Colours" type="number" req={isEdit} testId="mjc-no_of_colors" value={form.no_of_colors} onChange={(v) => upd("no_of_colors", v)} />
        <MjcSelectAdd label="Type of Varnish" optionsKey="varnish_type" req={isEdit} value={form.varnish_type} onChange={(v) => upd("varnish_type", v)} testId="mjc-varnish_type" />
        <MjcSelectAdd label="Print Type" optionsKey="print_type" req={isEdit} value={form.print_type || ""} onChange={(v) => upd("print_type", v)} testId="mjc-print_type" />
        <MjcSelectAdd label="Color Matching" optionsKey="color_matching" req={isEdit} value={form.color_matching || ""} onChange={(v) => upd("color_matching", v)} testId="mjc-color_matching" />
        <MjcSelectAdd label="Finishing" optionsKey="finishing_details" req={isEdit} value={form.finishing_details || ""} onChange={(v) => upd("finishing_details", v)} testId="mjc-finishing_details" />
        <MjcSelectAdd label="Embellishment" optionsKey="embellishment" req={isEdit} value={form.embellishment || ""} onChange={(v) => upd("embellishment", v)} testId="mjc-embellishment" />
        <MjcSelectAdd label="Form of Supply" optionsKey="form_of_supply" req={isEdit} value={form.form_of_supply || ""} onChange={(v) => upd("form_of_supply", v)} testId="mjc-form_of_supply" />
        {/* What "Form of Supply" is asking for next — one roll figure, or a sheet's two — shown right where it's picked. */}
        {form.form_of_supply === "Roll Form" && (
          <MjcField label="No. of Labels Per Roll" type="number" req={isEdit} testId="mjc-labels_per_roll" value={form.labels_per_roll} onChange={(v) => upd("labels_per_roll", v)} />
        )}
        {form.form_of_supply === "Sheet Form" && (
          <>
            <MjcField label="No. of Sheets Per Bundle" type="number" req={isEdit} testId="mjc-sheets_per_bundle" value={form.sheets_per_bundle} onChange={(v) => upd("sheets_per_bundle", v)} />
            <MjcField label="No. of Labels Per Sheet" type="number" req={isEdit} testId="mjc-labels_per_sheet" value={form.labels_per_sheet} onChange={(v) => upd("labels_per_sheet", v)} />
          </>
        )}
        <MjcArea label="Colours details (if special)" testId="mjc-colour_details" value={form.colour_details} onChange={(v) => upd("colour_details", v)} full />

        <MjcSubHeading label="Colours (up to 10)" />
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <MjcCombo
            key={`colour_${n}`}
            label={`Colour ${n}`}
            optionsKey="colour"
            testId={`mjc-colour_${n}`}
            value={form[`colour_${n}`] || ""}
            onChange={(v) => upd(`colour_${n}`, v)}
          />
        ))}

        <MjcSubHeading label="Finishing details" />
        <MjcCheckGroup
          label="Printing on"
          options={[
            { key: "print_one_side", label: "One Side", testId: "mjc-print_one_side" },
            { key: "print_front_back", label: "Front & Back", testId: "mjc-print_front_back" },
          ]}
          form={form}
          upd={upd}
        />
        <MjcField label="Any special" testId="mjc-any_special" value={form.any_special} onChange={(v) => upd("any_special", v)} />

        <MjcSubHeading label="Cylinder Repeat (Teeth ↔ Inches ↔ MM linked)" />
        <MjcCylinderRepeat form={form} setForm={setForm} />

        <MjcYesNo label="Serial Numbering" keyName="serial_numbering" form={form} upd={upd} />

        <MjcSubHeading label="Artwork details" />
        <MjcYesNo label="Artwork details received" keyName="artwork_details_received" form={form} upd={upd} />
        <MjcField label="Artwork No." testId="mjc-artwork_no" value={form.artwork_no} onChange={(v) => upd("artwork_no", v)} />
        <MjcField label="Scanning (T.P. Photo Colour)" testId="mjc-scanning_notes" value={form.scanning_notes} onChange={(v) => upd("scanning_notes", v)} />
        <MjcField label="Between Gap" testId="mjc-between_gap" value={form.between_gap} onChange={(v) => upd("between_gap", v)} />
        <MjcField label="PDF / File Name AVT" testId="mjc-pdf_file_name" value={form.pdf_file_name} onChange={(v) => upd("pdf_file_name", v)} />

        <div className="col-span-full">
          <label className="label-caps block mb-2">Label Roll Orientation — tick the required layout</label>
          <div className="border border-neutral-200 rounded-sm bg-white p-3 overflow-x-auto">
            <div className="grid grid-cols-9 gap-1 items-end min-w-[720px] max-w-5xl mx-auto">
              {[...LO_LI_TICKS.slice(0, 4), "SUN", ...LO_LI_TICKS.slice(4)].map((t) => {
                if (t === "SUN") {
                  return (
                    <div key="sun" className="flex flex-col items-center justify-end pb-6" aria-hidden="true">
                      <img src="/rolls/sun.png" alt="" className="h-16 sm:h-20 object-contain opacity-90" />
                    </div>
                  );
                }
                const on = !!(form.lo_li_ticks || {})[t];
                return (
                  <label
                    key={t}
                    className="flex flex-col items-center gap-1 cursor-pointer select-none group"
                  >
                    {/* Fixed box, not just a fixed height — a height-only constraint lets the wider
                        icons (LI-4, LO-1) get capped by the grid column's max-width before height,
                        so object-contain letterboxed them smaller than the narrower icons. */}
                    <div className="w-[117px] h-24 sm:w-[137px] sm:h-28 flex items-center justify-center">
                      <img
                        src={`/rolls/${t.toLowerCase()}-v2.png`}
                        alt={`Roll orientation ${t}`}
                        className="max-w-full max-h-full w-auto h-auto object-contain transition-transform group-hover:scale-105"
                        data-testid={`mjc-roll-${t.toLowerCase()}`}
                      />
                    </div>
                    <span className="text-[11px] font-semibold font-mono-num text-neutral-700 tracking-wide">{t}</span>
                    <input
                      type="checkbox"
                      data-testid={`mjc-tick-${t.toLowerCase()}`}
                      checked={on}
                      onChange={() => upd("lo_li_ticks", { ...(form.lo_li_ticks || {}), [t]: !on })}
                      className="accent-[#FF5A00] h-4 w-4 mt-0.5"
                    />
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-span-full">
          <label className="label-caps block mb-1.5">Core Diameter</label>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {["25 mm", "40 mm", "70 mm", "76 mm"].map((opt) => {
              const on = form.core_diameter === opt;
              return (
                <label key={opt} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`mjc-core_diameter-${opt.replace(/\s+/g, "").toLowerCase()}`}
                    checked={on}
                    onChange={() => upd("core_diameter", on ? "" : opt)}
                    className="accent-[#FF5A00] h-4 w-4"
                  />
                  <span className="font-medium">{opt}</span>
                </label>
              );
            })}
            <span className="text-xs text-neutral-500">If other, please specify →</span>
            <Input
              data-testid="mjc-core_other"
              value={form.core_other || ""}
              onChange={(e) => upd("core_other", e.target.value)}
              placeholder="e.g. 50 mm, 100 mm"
              className="rounded-sm h-8 text-sm w-56"
            />
          </div>
        </div>
        <MjcArea label="Special winding instructions" testId="mjc-winding_instructions" value={form.winding_instructions} onChange={(v) => upd("winding_instructions", v)} full />

        <MjcStatusDate label="Proof Submitted" statusKey="proof_submitted_status" dateKey="proof_submitted_date" form={form} upd={upd} />
        <MjcStatusDate label="Proof Approved" statusKey="proof_approved_status" dateKey="proof_approved_date" form={form} upd={upd} />
        <MjcStatusDate label="Shade card to be submitted" statusKey="shade_card_sent_status" dateKey="shade_card_sent" form={form} upd={upd} />

        <MjcSubHeading label="Customer contact person" />
        <MjcField label="Name" testId="mjc-contact_person" value={form.contact_person} onChange={(v) => upd("contact_person", v)} />
        <MjcField label="Phone No." testId="mjc-contact_phone" value={form.contact_phone} onChange={(v) => upd("contact_phone", v)} />
      </MjcSection>

      {/* Delivery Details */}
      <MjcSection title="Delivery Details" hint="Delivery schedule, place of delivery and other special instructions">
        <MjcArea label="" testId="mjc-delivery_details" value={form.delivery_details} onChange={(v) => upd("delivery_details", v)} full rows={3} />
      </MjcSection>

      {/* Marketing sign-off — one-click stamp */}
      <MjcSection title="Marketing Department">
        <div className="col-span-full">
          <SignBlock
            testIdBase="mjc-marketing"
            label="Marketing sign-off"
            signedBy={signatures.marketing_signed_by || form.marketing_signed_by}
            signedAt={signatures.marketing_signed_at || form.marketing_signed_date}
            history={signHistory.filter((e) => e.block === "marketing")}
            disabled={!isEdit}
            onSign={() => signBlock("marketing")}
            currentUserName={user?.name}
          />
        </div>
      </MjcSection>

      {/* Work Execution */}
      <MjcSection title="Work Execution Details" hint="To be filled in by the Head of the Operations Department">
        <MjcAvailability form={form} upd={upd} />
        <MjcField label="No. of Ups Planned" type="number" testId="mjc-no_of_ups" value={form.no_of_ups} onChange={(v) => upd("no_of_ups", v)} />
        <MjcField label="UPS Across" type="number" testId="mjc-ups_across" value={form.ups_across} onChange={(v) => upd("ups_across", v)} />
        <MjcField label="UPS Down" type="number" testId="mjc-ups_along" value={form.ups_along} onChange={(v) => upd("ups_along", v)} />
        <MjcField label="Gap Across (mm)" type="number" testId="mjc-gap_across_mm" value={form.gap_across_mm} onChange={(v) => upd("gap_across_mm", v)} />
        <div>
          <MjcField label="Planned Width (mm)" type="number" testId="mjc-paper_width_mm" value={form.paper_width_mm} onChange={(v) => upd("paper_width_mm", v)} />
          {Number(form.label_width_mm) > 0 && Number(form.ups_across) > 0 && (
            <div className="text-[10px] text-neutral-400 mt-1">
              Calculated: (label {form.label_width_mm}mm + gap {form.gap_across_mm || 0}mm) × {form.ups_across} ups + 12mm trim
              — pick an "In stock" width below rather than relying on this alone.
            </div>
          )}
          <AvailableWidths paperCode={form.substrate_code} onPick={(w) => setForm((f) => ({ ...f, paper_width_mm: w, stock_width_mm: w }))} />
        </div>
        <MjcCombo label="Punching Die Code No." optionsKey="die_code" testId="mjc-die_code" value={form.die_code} onChange={(v) => upd("die_code", v)} />
        <MjcCombo label="Die Size" optionsKey="die_size" testId="mjc-die_size" value={form.die_size} onChange={(v) => upd("die_size", v)} />
        <MjcField label="No. of Ups (Die)" type="number" testId="mjc-no_of_ups_die" value={form.no_of_ups_die} onChange={(v) => upd("no_of_ups_die", v)} />

        {/* Anilox Table — Pantone is read-only here (live from the colour boxes above); Anilox
            Details and Ink Manufacturer are whatever Printing last set, carried for the next repeat job. */}
        <div className="col-span-full">
          <label className="label-caps block mb-1.5">Anilox Used</label>
          <div className="border border-neutral-200 overflow-x-auto rounded-sm">
            <table className="w-full text-xs" data-testid="mjc-anilox-table">
              <thead className="bg-neutral-50 label-caps">
                <tr>
                  <th className="px-2 py-2 text-left">Unit No.</th>
                  <th className="px-2 py-2 text-left">Pantone Colour Code</th>
                  <th className="px-2 py-2 text-left">Anilox Details</th>
                  <th className="px-2 py-2 text-left">Ink Manufacturer</th>
                </tr>
              </thead>
              <tbody>
                {aniloxRows(form.anilox_details, form).map((row, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="px-2 py-1 font-mono-num text-neutral-500 w-16">{row.slot}</td>
                    <td className="px-2 py-1 font-mono-num text-neutral-700">{row.pantone_code || "—"}</td>
                    <td className="px-1 py-1">
                      <select data-testid={`mjc-anilox-${i}-combo`} value={row.anilox_combo}
                        onChange={(e) => updAnilox(i, "anilox_combo", e.target.value)}
                        className="w-full rounded-sm h-8 px-2 border border-neutral-200 bg-white text-xs font-mono-num">
                        <option value="">—</option>
                        {ANILOX_COMBOS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <select data-testid={`mjc-anilox-${i}-ink`} value={row.ink_manufacturer}
                        onChange={(e) => updAnilox(i, "ink_manufacturer", e.target.value)}
                        className="w-full rounded-sm h-8 px-2 border border-neutral-200 bg-white text-xs">
                        <option value="">—</option>
                        {INK_MANUFACTURERS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-full">
          <SignBlock
            testIdBase="mjc-operations"
            label="Head of Operations sign-off"
            signedBy={signatures.operations_signed_by || form.operations_signed_by}
            signedAt={signatures.operations_signed_at}
            history={signHistory.filter((e) => e.block === "operations")}
            disabled={!isEdit}
            onSign={() => signBlock("operations")}
            currentUserName={user?.name}
          />
        </div>
      </MjcSection>

      {/* Follow-up */}
      <MjcSection title="Shade Card Follow-up">
        <MjcStatusDate label="Shade card submitted on" statusKey="shade_card_submitted_status" dateKey="shade_card_submitted_on" form={form} upd={upd} />
        <MjcStatusDate label="Shade card received duly approved on" statusKey="shade_card_approved_status" dateKey="shade_card_approved_on" form={form} upd={upd} />
      </MjcSection>

      {/* Order fields — only for Job mode (an inquiry doesn't have PO yet) */}
      {!isInquiry && (
        <MjcSection title="Order Details">
          <MjcField label="Order Qty *" type="number" testId="mjc-quantity" value={form.quantity} onChange={(v) => upd("quantity", v)} required />
          <div>
            <MjcField label="Required Metres (Good)" type="number" testId="mjc-planned_good_meter" value={form.planned_good_meter} onChange={(v) => upd("planned_good_meter", v)} />
            {Number(form.quantity) > 0 && Number(form.ups_across) > 0 && Number(form.ups_along) > 0 && Number(form.cylinder_mm) > 0 && (
              <div className="text-[10px] text-neutral-400 mt-1">
                Calculated: ceil({form.quantity} qty ÷ {Number(form.ups_across) * Number(form.ups_along)} ups per repeat) × {form.cylinder_mm}mm cylinder
              </div>
            )}
            {!(Number(form.ups_across) > 0 && Number(form.ups_along) > 0 && Number(form.cylinder_mm) > 0) && (
              <div className="text-[10px] text-neutral-400 mt-1">
                Fill Ups Across, Ups Along and Cylinder (mm) above to auto-calculate this.
              </div>
            )}
          </div>
          <MjcField label="Allocated Waste Metres" type="number" testId="mjc-planned_waste_meter" value={form.planned_waste_meter} onChange={(v) => upd("planned_waste_meter", v)} />
          <div>
            <MjcField label="Excess Qty (m)" type="number" testId="mjc-excess_qty" value={form.excess_qty} onChange={(v) => upd("excess_qty", v)} />
            {(Number(form.planned_good_meter) > 0 || Number(form.planned_waste_meter) > 0 || Number(form.excess_qty) > 0) && (
              <div className="text-[10px] text-neutral-400 mt-1">
                Stores' Required Quantity = {Number(form.planned_good_meter) || 0} good + {Number(form.planned_waste_meter) || 0} waste + {Number(form.excess_qty) || 0} excess
                = <b>{(Number(form.planned_good_meter) || 0) + (Number(form.planned_waste_meter) || 0) + (Number(form.excess_qty) || 0)}</b> m
              </div>
            )}
          </div>
          <MjcField label="Unit Price (₹)" type="number" testId="mjc-unit_price" value={form.unit_price} onChange={(v) => upd("unit_price", v)} />
          <MjcField label={requiresPo ? "PO Number *" : "PO Number"} testId="mjc-po_number" value={form.po_number} onChange={(v) => upd("po_number", v)} required={requiresPo} />
          <MjcDate label="PO Date" testId="mjc-po_date" value={form.po_date} onChange={(v) => upd("po_date", v)} />
          <MjcDate label="HPDF Approved On" testId="mjc-hpdf_approved" value={form.hpdf_approved} onChange={(v) => upd("hpdf_approved", v)} />
          <PoMatches po={form.po_number} productCode={form.product_code} excludeId={initial?.id} />
          <MjcCombo label="Order Confirmed By" optionsKey="order_confirmed_by" testId="mjc-order_confirmed_by" value={form.order_confirmed_by} onChange={(v) => upd("order_confirmed_by", v)} />
          <MjcDate label="Date Received (order confirmed)" testId="mjc-date_received" value={form.date_received} onChange={(v) => upd("date_received", v)} />
          <MjcDate label="BPR Issue Date" testId="mjc-bpr_issue_date" value={form.bpr_issue_date} onChange={(v) => upd("bpr_issue_date", v)} />
          <MjcDate label="Job Due Date" testId="mjc-due_date" value={form.due_date} onChange={(v) => upd("due_date", v)} />
        </MjcSection>
      )}

      {/* Repeat Order Log — page 2 of the Annexure-I PDF. Only shown for Jobs (not Inquiries). */}
      {!isInquiry && (
        <MjcRepeatOrderLog
          rows={form.repeat_orders}
          onChange={(rows) => upd("repeat_orders", rows)}
        />
      )}

      <DialogFooter className="border-t border-neutral-200 pt-4 flex-wrap gap-2">
        {isEdit && (
          <>
            <Button type="button" variant="outline" onClick={() => openPreview("master-card.pdf")} data-testid="mjc-preview-card" className="rounded-sm">
              <Eye size={14} className="mr-1.5" /> Preview PDF
            </Button>
            <Button type="button" variant="outline" onClick={printCard} data-testid="mjc-print-card" className="rounded-sm">
              <Printer size={14} className="mr-1.5" /> Print Master Card
            </Button>
            {!!initial?.mpwo_number && (
              <Button type="button" variant="outline" onClick={printBpr} data-testid="mjc-print-bpr" className="rounded-sm">
                <Printer size={14} className="mr-1.5" /> Print BPR Slip
              </Button>
            )}
          </>
        )}
        {/* Two plain, unambiguous buttons — no dropdown, no "pick then click" step, so there's
            no way to click the wrong one by accident. For a new job (Operations Head only):
            "Save as BPR" (full validation, issues the real MPWO) vs "Save as To be planned" (no
            BPR, no required-field check — details aren't known yet). For an existing "Job ID
            only" job (no BPR, see above): "Save as BPR" here means Convert to BPR — full
            validation, issues the real MPWO, page then shows the normal full job view — vs
            "Save as To be planned" which just keeps it as-is, still no BPR, for partial
            progress. Everyone else just gets the one plain Save button. */}
        {!isEdit && !isInquiry && user?.role === "operations_head" && (
          <Button type="button" variant="outline" onClick={(e) => submit(e, "jobid")} disabled={saving}
            data-testid="mjc-save-jobid" className="rounded-sm">
            {saving ? "Saving…" : "Save as To be planned"}
          </Button>
        )}
        {isEdit && !initial?.mpwo_number && (
          <Button type="button" variant="outline" onClick={(e) => submit(e, "bpr")} disabled={saving}
            data-testid="mjc-save-draft" className="rounded-sm">
            {saving ? "Saving…" : "Save as To be planned"}
          </Button>
        )}
        <Button
          type="button"
          onClick={(e) => {
            // A real, permanent BPR/MPWO is about to be issued, and "Save as To be planned" was
            // sitting right next to this button as the alternative — worth one extra confirmation
            // so it's never issued by a misclick, since there's no undo for a real BPR number.
            const issuingBpr = isEdit ? !initial?.mpwo_number : (!isInquiry && user?.role === "operations_head");
            if (issuingBpr && !window.confirm("This will issue a real BPR/MPWO number for this job. Continue?")) return;
            submit(e, isEdit && !initial?.mpwo_number ? "convert" : "bpr");
          }}
          disabled={saving}
          data-testid="mjc-submit"
          className={cn("rounded-sm",
            isEdit && !initial?.mpwo_number ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#FF5A00] hover:bg-[#e04f00]")}
        >
          {isEdit && !initial?.mpwo_number ? <CheckCircle2 size={14} className="mr-1.5" /> : isEdit ? <Save size={14} className="mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
          {saving
            ? (isEdit && !initial?.mpwo_number ? "Converting…" : "Saving…")
            : isEdit && !initial?.mpwo_number ? "Save as BPR"
            : isEdit ? "Save Changes"
            : isInquiry ? "Save Inquiry"
            : !isInquiry && user?.role === "operations_head" ? "Save as BPR"
            : "Save Job"}
        </Button>
      </DialogFooter>

      <Dialog open={!!askGaps} onOpenChange={(v) => !v && setAskGaps(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="mjc-gaps-dialog">
          <DialogHeader>
            <DialogTitle>A few details are still empty</DialogTitle>
            <DialogDescription>Production and billing use these. Fill them in now, or save and add them later.</DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-1">
            {(askGaps || []).map((g) => (
              <li key={g.key}><button type="button" className="underline decoration-dotted hover:text-[#FF5A00]" onClick={() => goTo(g.key)} data-testid={`mjc-gap-${g.key}`}>{g.label}</button></li>
            ))}
          </ul>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => goTo(askGaps[0].key)} data-testid="mjc-gaps-fill">Fill them in</Button>
            <Button type="button" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" onClick={save} data-testid="mjc-gaps-save">Save anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline PDF preview panel */}
      <Sheet open={previewOpen} onOpenChange={(v) => { setPreviewOpen(v); if (!v && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl p-0" data-testid="mjc-preview-sheet">
          <SheetHeader className="p-4 border-b border-neutral-200 bg-neutral-50">
            <SheetTitle className="text-sm">Master Job Card — Preview</SheetTitle>
          </SheetHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Master Job Card Preview"
              data-testid="mjc-preview-iframe"
              className="w-full h-[calc(100vh-72px)]"
            />
          )}
        </SheetContent>
      </Sheet>
    </form>
  );
}

// ------------------------------------------------------------------- primitives
function MjcSection({ title, hint, children }) {
  return (
    <section className="border border-neutral-200 rounded-sm">
      <header className="bg-neutral-100 px-3 py-2 border-b border-neutral-200">
        <div className="font-bold text-xs uppercase tracking-wider">{title}</div>
        {hint && <div className="text-[10px] text-neutral-500 mt-0.5 italic">({hint})</div>}
      </header>
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {children}
      </div>
    </section>
  );
}

// Date picker version of MjcField — shares MjcField's label styling so the
// grid stays flush.
function MjcDate({ label, testId, value, onChange, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <DatePicker value={value} onChange={onChange} testId={testId} />
    </div>
  );
}

function MjcSubHeading({ label }) {
  return (
    <div className="col-span-full mt-1 -mb-1 text-[10px] font-bold uppercase tracking-wider text-[#FF5A00] border-b border-orange-100 pb-1">
      {label}
    </div>
  );
}

function MjcLabel({ label, req }) {
  return (
    <label className="label-caps block mb-1.5">
      {label}
      {req && <span className="text-rose-600 ml-0.5">*</span>}
    </label>
  );
}

function MjcField({ label, testId, value, onChange, type = "text", required, full, req }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <MjcLabel label={label} req={req} />
      <Input
        data-testid={testId}
        type={type}
        value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className={`rounded-sm h-9 ${type === "number" || type === "date" ? "font-mono-num" : ""}`}
      />
    </div>
  );
}

// Searchable drop-down over a list that fills itself from the jobs (die sizes, die codes, colours, who confirmed).
function MjcCombo({ label, optionsKey, value, onChange, testId, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <OptionCombobox optionsKey={optionsKey} value={value || ""} onChange={onChange} testId={testId} />
    </div>
  );
}

function MjcArea({ label, testId, value, onChange, full, rows = 2 }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <Textarea
        data-testid={testId}
        value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="rounded-sm resize-none"
      />
    </div>
  );
}

// MJC Select-With-Add wrapper — matches MjcField's label / spacing so the
// grid stays aligned. All new "add option" fields (print_type, varnish,
// embellishment, etc.) go through this so operators can extend the master
// lists without leaving the form.
function MjcSelectAdd({ label, optionsKey, value, onChange, testId, full, req }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      {label && <MjcLabel label={label} req={req} />}
      <SelectWithAdd optionsKey={optionsKey} value={value} onChange={onChange} testId={testId} placeholder="Pick or add…" />
    </div>
  );
}



// Real stock widths for this substrate, so Paper Width is picked from what's actually available
// rather than guessed — checked against real jobs, no formula reliably predicts this figure (the
// implied edge margin for a single-up label ranged from ~19mm to ~290mm across real jobs), so it's
// a stock-availability decision, not a calculation. Clicking a chip fills Paper Width with it.
function AvailableWidths({ paperCode, onPick }) {
  const [widths, setWidths] = useState([]);
  useEffect(() => {
    if (!paperCode) { setWidths([]); return; }
    api.get("/stores/widths", { params: { paper_code: paperCode } }).then(({ data }) => setWidths(data)).catch(() => setWidths([]));
  }, [paperCode]);
  if (!paperCode || widths.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      <span className="text-[10px] text-neutral-400 uppercase tracking-wider self-center">In stock:</span>
      {widths.map((w) => (
        <button
          key={w.item_code}
          type="button"
          data-testid={`mjc-width-chip-${w.width}`}
          onClick={() => onPick(w.width)}
          title={`${w.balance.toLocaleString("en-IN")} ${w.uom === "KG" ? "kg" : "m"} across ${w.rolls} roll(s)`}
          className="text-[11px] font-mono-num px-1.5 py-0.5 border border-neutral-300 rounded-sm hover:border-[#FF5A00] hover:text-[#FF5A00]"
        >
          {w.width}mm
        </button>
      ))}
    </div>
  );
}

function MjcCheckGroup({ label, options, form, upd }) {
  return (
    <div>
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        {options.map((opt) => (
          <label key={opt.key} className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid={opt.testId}
              checked={!!form[opt.key]}
              onChange={(e) => upd(opt.key, e.target.checked)}
              className="accent-[#FF5A00] h-4 w-4"
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function MjcYesNo({ label, keyName, form, upd }) {
  const value = form[keyName];
  const set = (v) => upd(keyName, value === v ? "" : v); // toggle-off if clicked again
  return (
    <div>
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <div className="flex items-center gap-3 pt-1">
        {["yes", "no"].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid={`mjc-${keyName}-${opt}`}
              checked={value === opt}
              onChange={() => set(opt)}
              className="accent-[#FF5A00] h-4 w-4"
            />
            <span className="capitalize font-medium">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Availability check tri-state — Yes / No / N/A per resource (Plate, Die,
// Material, Ink). Persists as "Yes" | "No" | "NA" strings so the same values
// flow to the Planning Spec `AvailBadge` colour scheme without translation.
function MjcAvailability({ form, upd }) {
  const items = [
    { key: "plate_available",    label: "Plate Available" },
    { key: "die_available",      label: "Die Available" },
    { key: "material_available", label: "Material Available" },
    { key: "ink_available",      label: "Ink Available" },
  ];
  return (
    <div className="col-span-full border border-orange-200 bg-orange-50/40 rounded-sm p-3" data-testid="mjc-availability">
      <div className="label-caps mb-2 text-[10px] text-[#FF5A00]">Availability Check</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(({ key, label }) => {
          const v = form[key] || "";
          // Static accent classes — dynamic `accent-${tone}` strings are
          // stripped by Tailwind's JIT scanner so tints render as the default.
          const opt = (val, cls) => (
            <label key={val} className="inline-flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="radio"
                data-testid={`mjc-${key}-${val.toLowerCase()}`}
                name={key}
                checked={v === val}
                onChange={() => upd(key, val)}
                className={cls}
              />
              <span>{val}</span>
            </label>
          );
          return (
            <div key={key}>
              <div className="text-[11px] font-medium mb-1">{label}</div>
              <div className="flex items-center gap-3">
                {opt("Yes", "accent-emerald-600")}
                {opt("No", "accent-rose-600")}
                {opt("NA", "accent-neutral-400")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MjcStatusDate({ label, statusKey, dateKey, form, upd }) {
  const status = form[statusKey] || "";
  const setStatus = (v) => upd(statusKey, status === v ? "" : v);
  return (
    <div className="col-span-full">
      {label && <label className="label-caps block mb-1.5">{label}</label>}
      <div className="flex flex-wrap items-center gap-4">
        {["yes", "no", "na"].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid={`mjc-${statusKey}-${opt}`}
              checked={status === opt}
              onChange={() => setStatus(opt)}
              className="accent-[#FF5A00] h-4 w-4"
            />
            <span className="font-medium">{opt === "na" ? "N/A" : opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
          </label>
        ))}
        <Input
          type="date"
          data-testid={`mjc-${dateKey}`}
          value={form[dateKey] || ""}
          onChange={(e) => upd(dateKey, e.target.value)}
          disabled={status === "na"}
          className="rounded-sm h-8 text-sm w-52"
          title={status === "na" ? "Date not applicable" : "Pick date"}
        />
      </div>
    </div>
  );
}


/* Cylinder Repeat lookup table — matches the Flexo pre-press cheat sheet.
   Selecting any one field will auto-fill the other two on a known row.
   Users can still add custom values via the SelectWithAdd dropdown. */
const CYL_REPEAT_ROWS = [
  { teeth: "73", inches: "9.13", mm: "231.775" },
  { teeth: "80", inches: "10.00", mm: "254" },
  { teeth: "84", inches: "10.50", mm: "266.7" },
  { teeth: "88", inches: "11.00", mm: "279.4" },
  { teeth: "90", inches: "11.25", mm: "285.75" },
  { teeth: "92", inches: "11.50", mm: "292.1" },
  { teeth: "95", inches: "11.88", mm: "301.625" },
  { teeth: "97", inches: "12.13", mm: "307.975" },
  { teeth: "98", inches: "12.25", mm: "311.15" },
  { teeth: "100", inches: "12.50", mm: "317.5" },
  { teeth: "104", inches: "13.00", mm: "330.2" },
  { teeth: "111", inches: "13.88", mm: "352.425" },
  { teeth: "116", inches: "14.50", mm: "368.3" },
  { teeth: "120", inches: "15.00", mm: "381" },
  { teeth: "128", inches: "16.00", mm: "406.4" },
  { teeth: "136", inches: "17.00", mm: "431.8" },
  { teeth: "160", inches: "20.00", mm: "508" },
  { teeth: "182", inches: "22.75", mm: "577.85" },
];

// Off the known table: teeth = mm / 3.175 (3.175mm = 1/8", the standard gear pitch), inches = teeth / 8.
// Keeps the other two fields filling in for any cylinder size, not just the cheat sheet's rows.
const round = (n, dp) => Math.round(n * 10 ** dp) / 10 ** dp;
function MjcCylinderRepeat({ form, setForm }) {
  const syncFrom = (col, v) => {
    const row = CYL_REPEAT_ROWS.find((r) => r[col] === v);
    const n = Number(v);
    const formula = !row && n > 0 ? (
      col === "mm" ? { teeth: round(n / 3.175, 0), inches: round(n / 3.175 / 8, 2) } :
      col === "teeth" ? { mm: round(n * 3.175, 3), inches: round(n / 8, 2) } :
      col === "inches" ? { teeth: round(n * 8, 0), mm: round(n * 8 * 3.175, 3) } : null
    ) : null;
    setForm((f) => ({
      ...f,
      cylinder_teeth: col === "teeth" ? v : (row ? row.teeth : formula ? String(formula.teeth) : f.cylinder_teeth),
      cylinder_inches: col === "inches" ? v : (row ? row.inches : formula ? String(formula.inches) : f.cylinder_inches),
      cylinder_mm: col === "mm" ? v : (row ? row.mm : formula ? String(formula.mm) : f.cylinder_mm),
    }));
  };
  return (
    <>
      <div>
        <label className="label-caps block mb-1.5">Cylinder Repeat — Teeth</label>
        <SelectWithAdd
          optionsKey="cylinder_teeth"
          value={form.cylinder_teeth}
          onChange={(v) => syncFrom("teeth", v)}
          testId="mjc-cylinder_teeth"
          placeholder="Pick or add teeth count"
        />
      </div>
      <div>
        <label className="label-caps block mb-1.5">Cylinder Repeat — Inches</label>
        <SelectWithAdd
          optionsKey="cylinder_inches"
          value={form.cylinder_inches}
          onChange={(v) => syncFrom("inches", v)}
          testId="mjc-cylinder_inches"
          placeholder="Pick or add inches"
        />
      </div>
      <div>
        <label className="label-caps block mb-1.5">Cylinder Repeat — MM</label>
        <SelectWithAdd
          optionsKey="cylinder_mm"
          value={form.cylinder_mm}
          onChange={(v) => syncFrom("mm", v)}
          testId="mjc-cylinder_mm"
          placeholder="Pick or add mm"
        />
      </div>
    </>
  );
}

/* Repeat Order Log — mirrors page 2 of the Annexure-I PDF. Rows are appended
   as the same product is re-run; each row captures the Marketing → Operation →
   Despatch trail plus a Remarks field. */
const REPEAT_COLS = [
  // group, key, header, width
  ["mkt", "wo_no",        "W.O. No.",        "min-w-[130px]"],
  ["mkt", "po_no",        "Customer PO No.", "min-w-[130px]"],
  ["mkt", "qty",          "Quantity",        "w-[90px]"],
  ["mkt", "delivery_date","Date of Delivery","w-[120px]", "date"],
  ["mkt", "mkt_sign",     "Sign.",           "w-[80px]"],
  ["op",  "print_date",   "Date of Printing","w-[120px]", "date"],
  ["op",  "issued_rmts",  "Issued RMTS",     "w-[100px]"],
  ["op",  "printed_rmts", "Printed RMTS",    "w-[100px]"],
  ["op",  "waste_rmts",   "Waste RMTS",      "w-[100px]"],
  ["op",  "waste_pct",    "Waste %",         "w-[70px]", "computed"],
  ["op",  "op_sign",      "Sign.",           "w-[80px]"],
  ["dsp", "dispatch_date","Date of Delivery","w-[120px]", "date"],
  ["dsp", "qty_delivered","Quantity Delivered","w-[110px]"],
  ["dsp", "dc_no",        "DC No. / Excise No.","min-w-[130px]"],
  ["dsp", "dispatch_sign","Sign.",           "w-[80px]"],
  ["rem", "remarks",      "Remarks",         "min-w-[160px]"],
];
const GROUP_LABELS = { mkt: "Marketing", op: "Operation (Printing)", dsp: "Despatch", rem: "Remarks" };
const GROUP_COLORS = { mkt: "bg-orange-50", op: "bg-blue-50", dsp: "bg-emerald-50", rem: "bg-neutral-50" };

function MjcRepeatOrderLog({ rows, onChange }) {
  const safeRows = Array.isArray(rows) && rows.length ? rows : [
    { sl: 1, wo_no: "", po_no: "", qty: "", delivery_date: "", mkt_sign: "",
      print_date: "", issued_rmts: "", printed_rmts: "", waste_rmts: "", op_sign: "",
      dispatch_date: "", qty_delivered: "", dc_no: "", dispatch_sign: "", remarks: "" },
  ];
  const upd = (idx, key, v) => {
    const next = safeRows.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [key]: v };
      // Auto-compute Waste RMTS = Issued − Printed whenever either changes.
      // Only overrides waste_rmts if the user hasn't manually locked it via '=' prefix.
      if ((key === "issued_rmts" || key === "printed_rmts") && !(String(r.waste_rmts || "").startsWith("="))) {
        const issued = parseFloat(updated.issued_rmts);
        const printed = parseFloat(updated.printed_rmts);
        if (Number.isFinite(issued) && Number.isFinite(printed)) {
          updated.waste_rmts = String(Math.max(0, issued - printed));
        }
      }
      return updated;
    });
    onChange(next);
  };
  const addRow = () => onChange([
    ...safeRows,
    { sl: safeRows.length + 1, wo_no: "", po_no: "", qty: "", delivery_date: "", mkt_sign: "",
      print_date: "", issued_rmts: "", printed_rmts: "", waste_rmts: "", op_sign: "",
      dispatch_date: "", qty_delivered: "", dc_no: "", dispatch_sign: "", remarks: "" },
  ]);
  const delRow = (idx) => {
    const next = safeRows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sl: i + 1 }));
    onChange(next.length ? next : [{ sl: 1, wo_no: "", po_no: "", qty: "", delivery_date: "", mkt_sign: "",
      print_date: "", issued_rmts: "", printed_rmts: "", waste_rmts: "", op_sign: "",
      dispatch_date: "", qty_delivered: "", dc_no: "", dispatch_sign: "", remarks: "" }]);
  };
  // Group header spans — count columns per group in the schema
  const groups = ["mkt", "op", "dsp", "rem"];
  const groupSpan = (g) => REPEAT_COLS.filter((c) => c[0] === g).length;

  return (
    <MjcSection
      title="Repeat Order Log"
      hint="Log every subsequent run of the same product — Marketing → Operation → Despatch. Mirrors page 2 of the Annexure-I Operations Work Order."
    >
      <div className="col-span-full overflow-x-auto" data-testid="mjc-repeat-log">
        <table className="w-full text-xs border border-neutral-300">
          <thead>
            <tr className="bg-neutral-100 text-neutral-700 uppercase text-[10px] tracking-wider">
              <th rowSpan="2" className="border border-neutral-300 px-2 py-1 w-[40px]">S. No.</th>
              {groups.map((g) => (
                <th key={g} colSpan={groupSpan(g)} className={`border border-neutral-300 px-2 py-1 ${GROUP_COLORS[g]}`}>
                  {GROUP_LABELS[g]}
                </th>
              ))}
              <th rowSpan="2" className="border border-neutral-300 px-2 py-1 w-[40px]">×</th>
            </tr>
            <tr className="bg-neutral-50 text-neutral-600 text-[10px]">
              {REPEAT_COLS.map(([g, k, hdr]) => (
                <th key={`${g}-${k}`} className={`border border-neutral-300 px-2 py-1 font-normal ${GROUP_COLORS[g]}`}>{hdr}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeRows.map((row, idx) => (
              <tr key={idx} className="hover:bg-neutral-50">
                <td className="border border-neutral-300 text-center font-mono-num text-neutral-500">{idx + 1}</td>
                {REPEAT_COLS.map(([g, k, hdr, w, type]) => {
                  if (type === "computed" && k === "waste_pct") {
                    const issued = parseFloat(row.issued_rmts);
                    const waste = parseFloat(row.waste_rmts);
                    const pct = Number.isFinite(issued) && issued > 0 && Number.isFinite(waste)
                      ? (waste / issued) * 100
                      : null;
                    const tone = pct == null ? "text-neutral-400"
                      : pct <= 3 ? "text-emerald-600"
                      : pct <= 6 ? "text-amber-600"
                      : "text-red-600";
                    return (
                      <td key={`${g}-${k}`} className={`border border-neutral-300 p-0 ${w || ""}`}>
                        <div
                          data-testid={`mjc-repeat-${idx}-waste_pct`}
                          className={`h-8 px-2 flex items-center justify-center text-xs font-semibold font-mono-num ${tone}`}
                          title={pct == null ? "Fill Issued & Printed to see waste %" : `Waste RMTS ${waste} of ${issued} issued`}
                        >
                          {pct == null ? "—" : `${pct.toFixed(1)}%`}
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={`${g}-${k}`} className="border border-neutral-300 p-0">
                      <input
                        type={type || "text"}
                        data-testid={`mjc-repeat-${idx}-${k}`}
                        value={row[k] || ""}
                        onChange={(e) => upd(idx, k, e.target.value)}
                        className={`${w || ""} h-8 px-2 text-xs border-0 focus:outline-none focus:ring-1 focus:ring-[#FF5A00] bg-transparent`}
                      />
                    </td>
                  );
                })}
                <td className="border border-neutral-300 text-center">
                  <button
                    type="button"
                    data-testid={`mjc-repeat-${idx}-del`}
                    onClick={() => delRow(idx)}
                    className="text-neutral-400 hover:text-red-600 px-2"
                    title="Remove row"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="mjc-repeat-add"
            onClick={addRow}
            className="rounded-sm text-xs"
          >
            <Plus size={12} className="mr-1" /> Add repeat order row
          </Button>
          <span className="text-[10px] text-neutral-500">
            {safeRows.length} row{safeRows.length === 1 ? "" : "s"} · saved with the job
          </span>
        </div>
      </div>
    </MjcSection>
  );
}




function MjcCustomer({ customers, form, onPick, onReload }) {
  const [addOpen, setAddOpen] = useState(false);
  const selected = customers.find((c) => c.id === form.customer_id) || (form.customer_id ? { id: form.customer_id, party_name: form.customer_name } : null);
  return (
    <div className="md:col-span-2">
      <label className="label-caps block mb-1.5">Name of the Customer *</label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <CustomerCombobox
            testId="mjc-customer"
            value={selected}
            initialCustomers={customers}
            onSelect={(c) => onPick(c)}
          />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              data-testid="mjc-add-customer-btn"
              variant="outline"
              size="sm"
              className="rounded-sm h-9 border-neutral-300 text-xs"
              title="Add a new customer"
            >
              <Plus size={13} className="mr-1" /> Add
            </Button>
          </DialogTrigger>
          <QuickCustomerDialog onDone={(cust) => {
            setAddOpen(false);
            // Reload the customers list; parent's `pickCustomer` runs when the new
            // id shows up in the select so all address fields hydrate correctly.
            onReload?.(cust?.id);
          }} />
        </Dialog>
      </div>
    </div>
  );
}


// ------------------------------------------------------- Sign-off block
function SignBlock({ testIdBase, label, signedBy, signedAt, history = [], disabled, onSign, currentUserName }) {
  const isSigned = !!(signedBy && signedBy.trim());
  const timestamp = signedAt ? new window.Date(signedAt).toLocaleString("en-IN", {
    dateStyle: "medium", timeStyle: "short",
  }) : "";
  const historyCount = history.length;
  return (
    <div className="flex items-center gap-3 border border-neutral-200 rounded-sm bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold w-40 shrink-0">
        {label}
      </div>
      {isSigned ? (
        <div className="flex items-center gap-2 flex-1" data-testid={`${testIdBase}-signed`}>
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold">{signedBy}</span>
            {timestamp && <span className="text-xs text-neutral-500 ml-2">signed {timestamp}</span>}
          </div>
          {historyCount > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid={`${testIdBase}-history`}
                  className="ml-1 rounded-sm text-xs text-neutral-600 h-7"
                >
                  <HistoryIcon size={12} className="mr-1" /> History
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-neutral-100 rounded-sm font-mono-num">{historyCount}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-3" data-testid={`${testIdBase}-history-popover`}>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
                  Signature audit trail
                </div>
                <ol className="space-y-2">
                  {[...history].reverse().map((e, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold text-neutral-900">{e.signed_by}</div>
                        <div className="text-neutral-500 font-mono-num text-[10px]">
                          {new window.Date(e.signed_at).toLocaleString("en-IN", {
                            dateStyle: "medium", timeStyle: "short",
                          })}
                        </div>
                      </div>
                      {i === 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-900 border border-emerald-200 uppercase tracking-wider">Latest</span>
                      )}
                    </li>
                  ))}
                </ol>
              </PopoverContent>
            </Popover>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid={`${testIdBase}-resign`}
            onClick={onSign}
            disabled={disabled}
            className="ml-auto rounded-sm text-xs text-neutral-600"
          >
            Re-sign as {currentUserName || "me"}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex-1 text-sm text-neutral-400 italic">Not yet signed</div>
          <Button
            type="button"
            size="sm"
            data-testid={`${testIdBase}-sign`}
            onClick={onSign}
            disabled={disabled}
            title={disabled ? "Save the card first, then sign" : ""}
            className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"
          >
            Sign as {currentUserName || "me"}
          </Button>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------- Autosave indicator
function AutosaveBadge({ status }) {
  const map = {
    idle:   { text: "Auto-save on",   color: "text-neutral-400" },
    saving: { text: "Saving…",         color: "text-neutral-500" },
    saved:  { text: "Saved",           color: "text-emerald-600" },
    error:  { text: "Save failed",     color: "text-rose-600" },
  };
  const s = map[status] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1 ${s.color}`} data-testid="mjc-autosave-badge">
      <Cloud size={11} /> {s.text}
    </span>
  );
}

